import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adLabsAPI } from '../../utils/api';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatHms(seconds) {
  if (!seconds || seconds <= 0) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

const STORAGE_KEY = (assessmentId, scenarioKey) =>
  scenarioKey ? `adlab_instance_${assessmentId}_${scenarioKey}` : `adlab_instance_${assessmentId}`;

// ─── styles ──────────────────────────────────────────────────────────────────

const S = {
  card: {
    background: '#1a1f2e',
    border: '1px solid #2d3448',
    borderRadius: '8px',
    padding: '1.25rem',
    marginTop: '1rem',
  },
  title: {
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#94a3b8',
    marginBottom: '1rem',
  },
  badge: (color) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: color === 'green' ? '#166534' : color === 'yellow' ? '#854d0e' : '#7f1d1d',
    color: color === 'green' ? '#86efac' : color === 'yellow' ? '#fde68a' : '#fca5a5',
  }),
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  },
  label: {
    fontSize: '0.8rem',
    color: '#64748b',
    minWidth: '110px',
  },
  value: {
    fontSize: '0.85rem',
    color: '#e2e8f0',
  },
  btn: (variant) => ({
    padding: '6px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
    background: variant === 'primary' ? '#2563eb'
      : variant === 'danger' ? '#dc2626'
      : '#334155',
    color: '#fff',
  }),
  link: {
    fontSize: '0.82rem',
    color: '#60a5fa',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  networkMap: {
    background: '#0f172a',
    border: '1px solid #1e293b',
    borderRadius: '6px',
    padding: '0.75rem',
    marginTop: '0.75rem',
  },
  networkTitle: {
    fontSize: '0.72rem',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.5rem',
  },
  networkRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: '0.8rem',
    color: '#94a3b8',
    fontFamily: 'monospace',
  },
  spinner: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid #334155',
    borderTopColor: '#60a5fa',
    borderRadius: '50%',
    animation: 'adlab-spin 0.8s linear infinite',
  },
  containerList: {
    marginTop: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  containerRow: (running) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.78rem',
    color: running ? '#86efac' : '#94a3b8',
  }),
  dot: (running) => ({
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: running ? '#22c55e' : '#475569',
    flexShrink: 0,
  }),
  errorText: {
    fontSize: '0.8rem',
    color: '#f87171',
    marginTop: '0.5rem',
  },
  countdown: {
    fontFamily: 'monospace',
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#34d399',
    letterSpacing: '0.05em',
  },
};

// Inject keyframe once
if (typeof document !== 'undefined' && !document.getElementById('adlab-keyframes')) {
  const style = document.createElement('style');
  style.id = 'adlab-keyframes';
  style.textContent = '@keyframes adlab-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

// ─── network map constant ────────────────────────────────────────────────────

const NETWORK_MAPS = {
  'ad-lateral-movement': [
    { name: 'DC01',  ip: '10.10.10.10', role: 'Domain Controller' },
    { name: 'SRV01', ip: '10.10.10.20', role: 'Member Server' },
    { name: 'WS01',  ip: '10.10.10.30', role: 'Workstation (entry)' },
  ],
  'ad-kerberoasting': [
    { name: 'DC01',  ip: '10.10.10.10', role: 'Domain Controller' },
    { name: 'WS01',  ip: '10.10.10.30', role: 'Workstation (entry)' },
  ],
  'hollow-crown': [
    { name: 'Kali',  ip: '10.10.10.99', role: 'Attacker terminal' },
    { name: 'WS01',  ip: '10.10.10.50', role: 'Win10 workstation (VLAN10)' },
    { name: 'SRV01', ip: '10.20.20.10', role: 'MSSQL server (VLAN20)' },
    { name: 'DC01',  ip: '10.30.30.10', role: 'Domain Controller (VLAN30)' },
  ],
};

// ─── component ───────────────────────────────────────────────────────────────

/**
 * AdLabPanel — recruiter control panel for the AD red-team lab.
 * Shown inside AssessmentDetails (right column, after Timeline).
 *
 * Props:
 *   assessment   — the full assessment object
 *   onLabStarted — optional callback(instanceId) when a lab starts
 */
function AdLabPanel({ assessment, scenarioKey, onLabStarted }) {
  const assessmentId = assessment?._id;
  const candidateId = assessment?.candidateId?._id || assessment?.candidateId;

  // Restore persisted instanceId so panel survives page refresh
  const [instanceId, setInstanceId] = useState(() => {
    if (!assessmentId) return null;
    return localStorage.getItem(STORAGE_KEY(assessmentId, scenarioKey)) || null;
  });

  const [labData, setLabData] = useState(null); // latest status response
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const pollRef = useRef(null);

  // ── persist instanceId ────────────────────────────────────────────────────
  useEffect(() => {
    if (!assessmentId) return;
    if (instanceId) {
      localStorage.setItem(STORAGE_KEY(assessmentId, scenarioKey), instanceId);
    } else {
      localStorage.removeItem(STORAGE_KEY(assessmentId, scenarioKey));
    }
  }, [instanceId, assessmentId]);

  // ── auto-discover existing lab for this assessment (e.g. auto-started on assignment) ──
  useEffect(() => {
    if (!assessmentId || instanceId) return; // already have one
    adLabsAPI.list().then(res => {
      const labs = res.data || [];
      // Use loose == so ObjectId toString() matches the string assessmentId prop
      const match = labs.find(l => String(l.assessmentId) === String(assessmentId) && !['stopped', 'expired', 'error'].includes(l.status));
      if (match) setInstanceId(match.instanceId);
    }).catch(() => {}); // silently ignore — recruiter can still start manually
  }, [assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── fetch status once + start/stop polling ────────────────────────────────
  const fetchStatus = useCallback(async (id) => {
    try {
      const res = await adLabsAPI.getStatus(id);
      setLabData(res.data);
      setError('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to fetch lab status';
      setError(msg);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((id, intervalMs) => {
    stopPolling();
    pollRef.current = setInterval(() => fetchStatus(id), intervalMs);
  }, [fetchStatus, stopPolling]);

  // On instanceId change: fetch immediately and set polling cadence
  useEffect(() => {
    if (!instanceId) return;
    fetchStatus(instanceId);
  }, [instanceId, fetchStatus]);

  // Adjust polling based on current status
  useEffect(() => {
    if (!instanceId || !labData) {
      stopPolling();
      return;
    }
    const status = labData.status;
    if (status === 'provisioning' || status === 'starting') {
      startPolling(instanceId, 8000);
    } else if (status === 'running') {
      startPolling(instanceId, 8000);
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [instanceId, labData?.status, startPolling, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── actions ───────────────────────────────────────────────────────────────
  const handleStart = async () => {
    setError('');
    setStarting(true);
    try {
      const timeLimitSec = assessment?.scheduling?.timeLimit || 7200;
      const durationMinutes = Math.round(timeLimitSec / 60);
      // scenarioKey prop overrides assessment-level scenario (supports multi-scenario assessments)
      const scenario = scenarioKey || assessment?.challenges?.scenario || null;
      const res = await adLabsAPI.start(candidateId, assessmentId, null, scenario, durationMinutes);
      const newId = res.data.instanceId;
      setInstanceId(newId);
      setLabData({ status: res.data.status, expiresAt: res.data.expiresAt });
      onLabStarted && onLabStarted(newId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start lab');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm('Stop this lab? The environment will be destroyed and cannot be recovered.')) return;
    setStopping(true);
    try {
      await adLabsAPI.stop(instanceId);
      setLabData(prev => ({ ...prev, status: 'stopped' }));
      stopPolling();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to stop lab');
    } finally {
      setStopping(false);
    }
  };

  const handleRestart = () => {
    // Clear old instance so the panel resets to "no lab" state
    setInstanceId(null);
    setLabData(null);
    setShareUrl(null);
    setShareEnabled(false);
    setError('');
  };

  const handleShare = async () => {
    setSharingBusy(true);
    try {
      const res = await adLabsAPI.createShare(instanceId);
      setShareUrl(res.data.shareUrl);
      setShareEnabled(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate share link');
    } finally {
      setSharingBusy(false);
    }
  };

  const handleToggleShare = async () => {
    setSharingBusy(true);
    try {
      const res = await adLabsAPI.setShareEnabled(instanceId, !shareEnabled);
      setShareEnabled(res.data.shareEnabled);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update share access');
    } finally {
      setSharingBusy(false);
    }
  };

  const handleCopyShare = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const handleOpenLab = async () => {
    try {
      const res = await adLabsAPI.connect(instanceId);
      const url = res.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open lab');
    }
  };

  // ── render helpers ────────────────────────────────────────────────────────
  const status = labData?.status || null;
  const isProvisioning = status === 'provisioning' || status === 'starting';
  const isRunning = status === 'running';
  const isStopped = status === 'stopped' || status === 'error' || status === 'expired';

  return (
    <section style={S.card}>
      <h2 style={S.title}>
        {scenarioKey === 'hollow-crown' ? 'Red Team Lab — Hollow Crown'
          : scenarioKey === 'ad-kerberoasting' ? 'Red Team Lab — AD Kerberoasting'
          : scenarioKey === 'ad-lateral-movement' ? 'Red Team Lab — AD Lateral Movement'
          : scenarioKey ? `Red Team Lab — ${scenarioKey}` : 'Red Team Lab (AD)'}
      </h2>

      {/* ── No lab started ── */}
      {!instanceId && !labData && (
        <div>
          <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '0.75rem' }}>
            Provision a full Active Directory environment for this candidate (2-hour session).
          </p>
          <button style={S.btn('primary')} onClick={handleStart} disabled={starting}>
            {starting ? 'Starting...' : 'Start AD Lab'}
          </button>
        </div>
      )}

      {/* ── Provisioning ── */}
      {isProvisioning && (
        <div>
          <div style={S.row}>
            <span style={S.badge('yellow')}>Provisioning</span>
            <span style={S.spinner} />
            <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Lab is starting...</span>
          </div>
          {labData?.containers?.length > 0 && (
            <div style={S.containerList}>
              {labData.containers.map((c) => {
                const running = c.status === 'running' || c.state === 'running';
                return (
                  <div key={c.name || c.id} style={S.containerRow(running)}>
                    <span style={S.dot(running)} />
                    <span>{c.name || c.id}</span>
                    <span style={{ color: '#475569' }}>{c.status || c.state}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Running ── */}
      {isRunning && (
        <div>
          <div style={S.row}>
            <span style={S.badge('green')}>Running</span>
            {labData.timeRemainingSeconds != null && (
              <span style={S.countdown}>{formatHms(labData.timeRemainingSeconds)}</span>
            )}
          </div>

          {/* Open lab button — mints a fresh Guacamole auth token on click */}
          <div style={{ ...S.row, marginBottom: '1rem' }}>
            <button style={S.btn('primary')} onClick={handleOpenLab}>
              Open Lab in Browser &#8599;
            </button>
          </div>

          {/* Network map */}
          {(() => {
            const networkMap = NETWORK_MAPS[scenarioKey] || NETWORK_MAPS['ad-lateral-movement'];
            const isMultiVlan = scenarioKey === 'hollow-crown';
            return (
              <div style={S.networkMap}>
                <div style={S.networkTitle}>
                  {isMultiVlan ? 'Network Map — 3-VLAN Isolated' : 'Network Map — 10.10.10.0/24'}
                </div>
                {networkMap.map((node) => (
                  <div key={node.name} style={S.networkRow}>
                    <span style={{ color: '#60a5fa', fontWeight: 600 }}>{node.name}</span>
                    <span>{node.ip}</span>
                    <span style={{ color: '#475569' }}>{node.role}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Share link */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid #1e293b', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
              Candidate Access Link
            </div>
            {!shareUrl ? (
              <button style={S.btn('primary')} onClick={handleShare} disabled={sharingBusy}>
                {sharingBusy ? 'Generating...' : 'Generate Share Link'}
              </button>
            ) : (
              <div>
                {/* Link row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.6rem' }}>
                  <input
                    readOnly
                    value={shareUrl}
                    style={{
                      flex: 1, background: '#0f172a', border: '1px solid #1e293b',
                      borderRadius: '5px', padding: '5px 8px', fontSize: '0.75rem',
                      color: '#94a3b8', fontFamily: 'monospace', outline: 'none'
                    }}
                    onFocus={e => e.target.select()}
                  />
                  <button style={S.btn('primary')} onClick={handleCopyShare}>
                    {shareCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                {/* Enable/disable toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={handleToggleShare}
                    disabled={sharingBusy}
                    style={{
                      ...S.btn(shareEnabled ? 'danger' : 'primary'),
                      fontSize: '0.78rem', padding: '4px 12px'
                    }}
                  >
                    {sharingBusy ? '...' : shareEnabled ? 'Revoke Access' : 'Restore Access'}
                  </button>
                  <span style={{ fontSize: '0.75rem', color: shareEnabled ? '#86efac' : '#f87171' }}>
                    {shareEnabled ? 'Active — candidate can connect' : 'Revoked — link is blocked'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Stop */}
          <div style={{ marginTop: '1rem' }}>
            <button style={S.btn('danger')} onClick={handleStop} disabled={stopping}>
              {stopping ? 'Stopping...' : 'Stop Lab'}
            </button>
          </div>
        </div>
      )}

      {/* ── Stopped / error / expired ── */}
      {isStopped && (
        <div>
          <div style={S.row}>
            <span style={S.badge('red')}>{(status || 'stopped').toUpperCase()}</span>
          </div>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
            The lab has been stopped or encountered an error.
          </p>
          <button style={S.btn('primary')} onClick={handleRestart}>
            Restart Lab
          </button>
        </div>
      )}

      {/* Error message */}
      {error && <p style={S.errorText}>{error}</p>}
    </section>
  );
}

export default AdLabPanel;
